import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { SUPPORTED_LANGUAGES, persistLanguage, readStoredLanguage } from '../i18n';

/**
 * Amazon-style compact language switcher for public nav bars.
 * Shows: [🇬🇧 EN ▾]. Click opens a small dropdown with radio-buttons.
 */
const LanguageSwitcher = ({ variant = 'light' }) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [, forceRerender] = useState(0);
  const ref = useRef(null);

  // The user's TRUE choice lives in localStorage (persistLanguage writes it there).
  // i18n.language may lag behind for non-hardcoded languages (which stay at 'en').
  const currentCode = (
    readStoredLanguage()
    || i18n.resolvedLanguage
    || i18n.language
    || 'en'
  ).split('-')[0];
  const current = SUPPORTED_LANGUAGES.find((l) => l.code === currentCode) || SUPPORTED_LANGUAGES[0];

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const select = (code) => {
    persistLanguage(code);
    setOpen(false);
    forceRerender((n) => n + 1); // pick up localStorage change for non-hardcoded langs
  };

  const isDark = variant === 'dark';
  const triggerCls = isDark
    ? 'text-gray-100 hover:text-white'
    : 'text-gray-700 hover:text-blue-500';

  return (
    <div ref={ref} className="relative" data-testid="nav-lang-switcher" translate="no">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${triggerCls}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="nav-lang-trigger"
      >
        <span className="text-base leading-none" aria-hidden="true">{current.flag}</span>
        <span className="uppercase tracking-wide text-[13px]">{current.code}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-[60]"
          role="listbox"
          data-testid="nav-lang-menu"
        >
          <div className="px-3.5 py-2 bg-gray-50 border-b border-gray-100">
            <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Change language</p>
          </div>
          <ul className="py-1 max-h-72 overflow-y-auto">
            {SUPPORTED_LANGUAGES.map((lang) => {
              const active = lang.code === currentCode;
              return (
                <li key={lang.code}>
                  <button
                    type="button"
                    onClick={() => select(lang.code)}
                    className={`w-full flex items-center gap-3 px-3.5 py-2 text-sm text-left transition ${
                      active ? 'bg-blue-50/70 text-blue-700 font-semibold' : 'text-gray-800 hover:bg-gray-50'
                    }`}
                    role="option"
                    aria-selected={active}
                    data-testid={`nav-lang-option-${lang.code}`}
                  >
                    <span
                      aria-hidden="true"
                      className={`w-3.5 h-3.5 rounded-full flex-shrink-0 border ${
                        active ? 'border-blue-500 bg-white ring-[3px] ring-blue-500/40' : 'border-gray-300'
                      }`}
                    />
                    <span className="text-base leading-none" aria-hidden="true">{lang.flag}</span>
                    <span className="flex-1">
                      {lang.label}
                      {!lang.hardcoded && (
                        <span className="ml-1.5 text-[10px] font-normal text-gray-400 uppercase tracking-wide">auto</span>
                      )}
                    </span>
                    <span className="text-[11px] uppercase tracking-wide text-gray-400">{lang.code}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
